import type { RiskAssessmentResponse } from '@gcv/shared';
import { useMutation } from '@tanstack/react-query';

import { RiskAssessmentsApi } from 'api/RiskAssessmentsApi';
import { useFiRiskAssessmentCache } from 'tanstack-hooks/fi-risk-assessments/risk-assessments-cache';
import { RISK_ASSESSMENTS_KEY } from 'tanstack-hooks/keys';
import { useUser } from 'tanstack-hooks/user';

interface SaveRiskAssessmentResponsesArgs {
  id: string;
  responses: { [field_id: string]: RiskAssessmentResponse };
}

export function useSaveRiskAssessmentResponses() {
  const riskAssessmentsApi = new RiskAssessmentsApi();
  const { userBankId } = useUser();
  const { setRiskAssessment } = useFiRiskAssessmentCache();

  const { mutateAsync: saveRiskAssessmentResponses, isPending: isSavingRiskAssessmentResponses } =
    useMutation({
      mutationKey: [RISK_ASSESSMENTS_KEY, 'save'],
      // Autosave: the score, rating, and section breakdown come back recomputed on the result.
      mutationFn: ({ id, responses }: SaveRiskAssessmentResponsesArgs) =>
        riskAssessmentsApi.saveRiskAssessmentResponses(userBankId, id, responses),
      onSuccess: (result) => {
        // Seed the recomputed result so the score rail updates without a refetch on every save.
        setRiskAssessment(result);
      },
      meta: {
        snackbarErrorMessage: 'There was an issue saving your answers. Please contact support.'
      }
    });

  return { saveRiskAssessmentResponses, isSavingRiskAssessmentResponses };
}
